import React from 'react';
import {
  Button,
  Heading,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
} from '@chakra-ui/react';
import { SystemTranslationKeys } from '@common/entities';

import { useSystemTranslation } from 'hooks';

interface CommonModalBaseProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  title: string;
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl';
  modalDescription?: string;
}

type CloseButtonProps =
  | { showCloseButton?: false; closeButtonText?: never; closeButtonVariant?: 'brand' | 'outline' }
  | {
      showCloseButton?: true;
      closeButtonText?: string;
      closeButtonVariant?: 'brand' | 'outline';
    };

type SubmitButtonProps =
  | {
      showSubmitButton?: false;
      submitButtonText?: never;
      onSubmit?: never;
      submitButtonVariant?: 'brand' | 'outline';
    }
  | {
      showSubmitButton?: true;
      submitButtonText?: string;
      onSubmit: () => void;
      submitButtonVariant?: 'brand' | 'outline';
    };

type Props = CommonModalBaseProps & CloseButtonProps & SubmitButtonProps;
const CommonModal = (props: Props) => {
  const { tSystem } = useSystemTranslation();
  const {
    isOpen,
    onClose,
    children,
    title,
    closeButtonText = tSystem(SystemTranslationKeys.common.close),
    submitButtonText = tSystem(SystemTranslationKeys.common.submit),
    onSubmit,
    showCloseButton = true,
    showSubmitButton = true,
    closeButtonVariant = 'brand',
    submitButtonVariant = 'brand',
    size = 'md',
    modalDescription,
  } = props;
  return (
    <Modal onClose={onClose} isOpen={isOpen} size={size}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Heading fontSize="lg">{title}</Heading>
          {modalDescription && (
            <Text fontSize="xs" mt={2}>
              {modalDescription}
            </Text>
          )}
        </ModalHeader>
        <ModalCloseButton aria-label={tSystem(SystemTranslationKeys.common.close)} />
        <ModalBody>{children}</ModalBody>
        <ModalFooter>
          {showCloseButton && (
            <Button variant={closeButtonVariant} onClick={onClose}>
              {closeButtonText}
            </Button>
          )}
          {showSubmitButton && (
            <Button variant={submitButtonVariant} onClick={onSubmit}>
              {submitButtonText}
            </Button>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default CommonModal;
